import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  LayoutDashboard, ClipboardList, Package, ShoppingCart, FileText, Settings,
  LogOut, RefreshCw, Bell, ChevronDown, User, Anchor, X, Plus
} from 'lucide-react';

// ── Navigation config ─────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', roles: null },
  { path: '/work-orders', label: 'Work Orders', roles: null },
  { path: '/inventory', label: 'Inventory', roles: null },
  { path: '/purchasing', label: 'Purchasing', roles: ['chief_engineer', 'second_engineer', 'procurement_officer', 'fleet_superintendent', 'technical_manager', 'shore_admin', 'iShip_admin'] },
  { path: '/reports', label: 'Reports', roles: ['chief_engineer', 'second_engineer', 'fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'] },
  { path: '/misc', label: 'Settings', roles: ['chief_engineer', 'second_engineer', 'electrical_officer', 'fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin', 'iShip_admin'] },
];

const PAGE_META = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Daily Briefing' },
  '/work-orders': { title: 'Work Orders', subtitle: 'Manage & Track' },
  '/work-orders/:id': { title: 'Work Order', subtitle: 'Detail' },
  '/maintenance-plan': { title: 'Maintenance Plan', subtitle: 'Planned Jobs' },
  '/work-plans': { title: 'Work Plans', subtitle: 'Scheduled Plans' },
  '/inventory': { title: 'Inventory', subtitle: 'Stock & Components' },
  '/purchasing': { title: 'Purchasing', subtitle: 'Requisitions & POs' },
  '/reports': { title: 'Reports', subtitle: 'Analytics & Export' },
  '/misc': { title: 'Settings', subtitle: 'System Configuration' },
};

// ── Sync Button ───────────────────────────────────────────────────────────────
function SyncButton({ compact }) {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(() => {
    const t = localStorage.getItem('marisync_last_sync');
    return t ? new Date(t) : null;
  });

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/sync/trigger');
      const now = new Date();
      setLastSync(now);
      localStorage.setItem('marisync_last_sync', now.toISOString());
    } catch (e) {
      console.error('Sync failed', e);
    } finally {
      setSyncing(false);
    }
  };

  const syncLabel = () => {
    if (!lastSync) return 'Never';
    const diff = Math.floor((Date.now() - lastSync) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  if (compact) {
    return (
      <button className="btn btn-sm btn-outline" onClick={handleSync} disabled={syncing} title="Sync data">
        <RefreshCw size={13} style={{ animation: syncing ? 'spin 0.8s linear infinite' : 'none' }} />
        {syncing ? 'Syncing...' : syncLabel()}
      </button>
    );
  }

  return (
    <button className="btn btn-outline btn-sm" onClick={handleSync} disabled={syncing}>
      <RefreshCw size={13} style={{ animation: syncing ? 'spin 0.8s linear infinite' : 'none' }} />
      {syncing ? 'Syncing...' : `Sync · ${syncLabel()}`}
    </button>
  );
}

// ── User Dropdown ─────────────────────────────────────────────────────────────
function UserDropdown({ user, vessel, tenant, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: open ? '#EBF3F9' : 'transparent',
          border: '1px solid var(--border)', borderRadius: 8,
          padding: '6px 10px', cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'linear-gradient(135deg, #85A3C1, #3B6082)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ textAlign: 'left', maxWidth: 140 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', lineHeight: 1.2 }}>
            {user?.full_name?.split(' ')[0]}
          </div>
          <div style={{ fontSize: 10, color: '#3B6082', lineHeight: 1.2 }}>
            {user?.role?.replace(/_/g, ' ')}
          </div>
        </div>
        <ChevronDown size={13} style={{ color: 'var(--text-secondary)', transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: 'white', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 220, zIndex: 300,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{user?.full_name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{user?.email}</div>
            <div style={{ fontSize: 10, color: '#3B6082', marginTop: 4, fontWeight: 600 }}>{user?.role?.replace(/_/g, ' ').toUpperCase()}</div>
          </div>
          {vessel && (
            <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-secondary)' }}>
              🚢 {vessel.name}
            </div>
          )}
          {tenant && (
            <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 11, color: '#9ca3af' }}>
              {tenant.company_name}
            </div>
          )}
          <div style={{ padding: 6 }}>
            <button
              onClick={() => { setOpen(false); if (window.confirm('Logout?')) logout(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer',
                borderRadius: 6, fontSize: 12, color: '#e74c3c', transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fdecea'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Role badge ────────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const colors = {
    iShip_admin: { bg: '#ede9fe', color: '#7c3aed' },
    shore_admin: { bg: '#dbeafe', color: '#2563eb' },
    technical_manager: { bg: '#e0f2fe', color: '#0284c7' },
    fleet_superintendent: { bg: '#d1fae5', color: '#059669' },
    procurement_officer: { bg: '#fef3c7', color: '#d97706' },
    chief_engineer: { bg: '#EBF3F9', color: '#3B6082' },
    second_engineer: { bg: '#e0f2fe', color: '#0284c7' },
    electrical_officer: { bg: '#ede9fe', color: '#7c3aed' },
    third_engineer: { bg: '#f1f5f9', color: '#64748b' },
    bosun: { bg: '#f1f5f9', color: '#78716c' },
  };
  const s = colors[role] || { bg: '#f1f5f9', color: '#6b7280' };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {role?.replace(/_/g, ' ')}
    </span>
  );
}

// ── Notification Bell ──────────────────────────────────────────────────────────
function NotificationBell() {
  const [count] = useState(0);
  return (
    <button className="btn btn-outline btn-sm" style={{ position: 'relative' }}>
      <Bell size={15} />
      {count > 0 && (
        <span style={{
          position: 'absolute', top: -4, right: -4,
          width: 16, height: 16, borderRadius: '50%',
          background: '#e74c3c', color: 'white', fontSize: 9, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
}

// ── Main Header ────────────────────────────────────────────────────────────────
function MainHeader({ user, vessel, tenant, logout, isShoreUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [ddOpen, setDdOpen] = useState(null);

  const canSee = (roles) => {
    if (!roles || !user) return true;
    return roles.includes(user.role);
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(path + '/') || location.pathname.startsWith(path + '?');
  };

  return (
    <header className="main-header">
      {/* Left: Logo */}
      <div className="header-brand" onClick={() => navigate('/dashboard')}>
        <img src={process.env.PUBLIC_URL + '/logo.png'} alt="iSHIP+" style={{ height: 32, objectFit: 'contain' }} />
        <div className="header-brand-text">
          <span className="header-brand-name">iSHIP+</span>
          <span className="header-brand-sub">MARISYNC PMS</span>
        </div>
      </div>

      {/* Center: Nav tabs */}
      <nav className="header-nav">
        {NAV_ITEMS.filter(item => canSee(item.roles)).map(item => (
          <div key={item.path} style={{ position: 'relative' }}>
            <button
              className={`header-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => {
                if (item.path === location.pathname) return;
                navigate(item.path);
              }}
            >
              {item.label}
            </button>
          </div>
        ))}
      </nav>

      {/* Right: Controls */}
      <div className="header-controls">
        {isShoreUser() && (
          <select className="header-vessel-select" defaultValue={vessel?.id || ''}>
            <option value="">All Vessels</option>
          </select>
        )}
        <SyncButton compact />
        <NotificationBell />
        <UserDropdown user={user} vessel={vessel} tenant={tenant} logout={logout} />
      </div>
    </header>
  );
}

// ── Sub-header ────────────────────────────────────────────────────────────────
function SubHeader({ title, subtitle }) {
  return (
    <div className="sub-header">
      <div className="sub-header-left">
        <h1 className="sub-header-title">{title}</h1>
        {subtitle && <span className="sub-header-sep">/</span>}
        {subtitle && <span className="sub-header-sub">{subtitle}</span>}
      </div>
      <div className="sub-header-right">
        <Outlet context={{}} />
      </div>
    </div>
  );
}

// ── Layout ───────────────────────────────────────────────────────────────────
export default function Layout() {
  const { user, vessel, tenant, logout, isShoreUser } = useAuth();
  const location = useLocation();

  const getPageMeta = () => {
    const pathname = location.pathname;
    // Try exact match first
    if (PAGE_META[pathname]) return PAGE_META[pathname];
    // Try prefix match for dynamic routes like /work-orders/:id
    for (const [key, val] of Object.entries(PAGE_META)) {
      if (key.includes(':')) {
        const base = key.split('/:')[0];
        if (pathname.startsWith(base)) return val;
      }
    }
    return { title: 'iSHIP+ MARISYNC', subtitle: '' };
  };

  const meta = getPageMeta();

  return (
    <div className="app-layout">
      {/* Main Header */}
      <MainHeader
        user={user}
        vessel={vessel}
        tenant={tenant}
        logout={logout}
        isShoreUser={isShoreUser}
      />

      {/* Content */}
      <div className="app-content">
        {/* Sub-header */}
        <div className="sub-header">
          <div className="sub-header-left">
            <h1 className="sub-header-title">{meta.title}</h1>
            {meta.subtitle && (
              <>
                <span className="sub-header-sep">/</span>
                <span className="sub-header-sub">{meta.subtitle}</span>
              </>
            )}
          </div>
          <div className="sub-header-right">
            {/* Pages can inject buttons here via Outlet context if needed */}
          </div>
        </div>

        {/* Page body */}
        <div className="page-body">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
